import React, { useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Table,
  Button,
  Badge,
  Spinner,
  Alert,
  Modal,
  Form,
} from "react-bootstrap";
import { FaChartBar, FaEye, FaTrash, FaSearch, FaFilter } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const SurveysManagement = () => {
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSurveyModal, setShowSurveyModal] = useState(false);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("all");
  const navigate = useNavigate();

  // Helper function to parse dates from various formats
  const parseDate = (dateValue) => {
    if (!dateValue) return null;

    // Handle Firestore Timestamp objects
    if (dateValue._seconds || dateValue.seconds) {
      const seconds = dateValue._seconds || dateValue.seconds;
      return new Date(seconds * 1000);
    }

    // Handle string dates
    if (typeof dateValue === "string") {
      const parsed = new Date(dateValue);
      return isNaN(parsed.getTime()) ? null : parsed;
    }

    // Handle Date objects
    if (dateValue instanceof Date) {
      return isNaN(dateValue.getTime()) ? null : dateValue;
    }

    // Handle numeric timestamps
    if (typeof dateValue === "number") {
      const parsed = new Date(dateValue);
      return isNaN(parsed.getTime()) ? null : parsed;
    }

    return null;
  };

  // Format date for display
  const formatDate = (dateValue) => {
    const date = parseDate(dateValue);
    return date ? date.toLocaleDateString() : "N/A";
  };

  // Format date with time for display
  const formatDateTime = (dateValue) => {
    const date = parseDate(dateValue);
    return date ? date.toLocaleString() : "N/A";
  };

  useEffect(() => {
    fetchSurveys();
  }, []);

  const fetchSurveys = async () => {
    try {
      setLoading(true);
      const token =
        localStorage.getItem("adminToken") ||
        sessionStorage.getItem("adminToken");

      // Use dynamic base URL approach like other admin components
      const bases = Array.from(
        new Set(
          [
            process.env.REACT_APP_API_BASE_URL,
            "https://api.quvikme.com",
          ].filter(Boolean)
        )
      );

      let surveysData = null;
      let lastErr = null;

      // Try to fetch surveys from each base URL until one works
      for (const base of bases) {
        try {
          const surveysResponse = await fetch(`${base}/api/surveys/admin/all`, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          });

          if (!surveysResponse.ok) {
            lastErr = new Error(`Failed to fetch from ${base}`);
            continue;
          }

          surveysData = await surveysResponse.json();
          break;
        } catch (e) {
          lastErr = e;
          continue;
        }
      }

      if (!surveysData)
        throw lastErr || new Error("Failed to fetch surveys from any endpoint");

      const surveysList = surveysData.surveys || [];

      // Extract unique user IDs
      const userIds = [...new Set(surveysList.map((survey) => survey.userId))];

      // Fetch user data for all users in surveys using the same dynamic approach
      const usersData = {};
      if (userIds.length > 0) {
        // Split userIds into chunks of 10 to avoid URL length limits
        const chunks = [];
        for (let i = 0; i < userIds.length; i += 10) {
          chunks.push(userIds.slice(i, i + 10));
        }

        // Fetch users in batches using dynamic base URL approach
        for (const chunk of chunks) {
          try {
            let usersResult = null;
            let lastUserErr = null;

            // Try each base URL for user data
            for (const base of bases) {
              try {
                const usersResponse = await fetch(
                  `${base}/api/users?userIds=${chunk.join(",")}`,
                  {
                    headers: {
                      Authorization: `Bearer ${token}`,
                      "Content-Type": "application/json",
                    },
                  }
                );

                if (!usersResponse.ok) {
                  lastUserErr = new Error(`Failed to fetch users from ${base}`);
                  continue;
                }

                usersResult = await usersResponse.json();
                break;
              } catch (err) {
                lastUserErr = err;
                continue;
              }
            }

            if (usersResult) {
              const users = usersResult.users || usersResult;

              if (Array.isArray(users)) {
                users.forEach((user) => {
                  usersData[user.id || user._id] = user;
                });
              }
            }
          } catch (err) {
            console.error("Failed to fetch user data for chunk:", err);
          }
        }
      }

      // Map surveys with user names and emails
      const surveysWithUserNames = surveysList.map((survey) => ({
        ...survey,
        userName: usersData[survey.userId]
          ? usersData[survey.userId].name ||
            usersData[survey.userId].displayName ||
            `${usersData[survey.userId].firstName || ""} ${
              usersData[survey.userId].lastName || ""
            }`.trim() ||
            "Unknown User"
          : "Unknown User",
        userEmail: usersData[survey.userId]
          ? usersData[survey.userId].email || "No Email"
          : "No Email",
      }));

      setSurveys(surveysWithUserNames);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const viewSurveyDetails = (survey) => {
    setSelectedSurvey(survey);
    setShowSurveyModal(true);
  };

  const getRatingText = (rating) => {
    switch (rating) {
      case 1:
        return "Poor";
      case 2:
        return "Fair";
      case 3:
        return "Good";
      case 4:
        return "Very Good";
      case 5:
        return "Excellent";
      default:
        return "N/A";
    }
  };

  const getAverageRating = (answers) => {
    if (!answers) return 0;
    const ratings = Object.values(answers);
    if (ratings.length === 0) return 0;
    const sum = ratings.reduce(
      (acc, rating) => acc + (typeof rating === "number" ? rating : 0),
      0
    );
    return (sum / ratings.length).toFixed(1);
  };

  const filteredSurveys = surveys.filter((survey) => {
    const matchesSearch =
      survey.userId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      survey.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (survey.userName &&
        survey.userName.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (survey.userEmail &&
        survey.userEmail.toLowerCase().includes(searchTerm.toLowerCase()));

    if (filter === "all") return matchesSearch;
    // Add more filters as needed

    return matchesSearch;
  });

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "400px" }}
      >
        <Spinner animation="border" variant="primary" />
        <span className="ms-2">Loading surveys...</span>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger">
        <h4>Error loading surveys</h4>
        <p>{error}</p>
        <Button onClick={fetchSurveys} variant="outline-primary">
          Retry
        </Button>
      </Alert>
    );
  }

  return (
    <div className="surveys-management p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>
          <FaChartBar className="me-2" />
          Survey Management
        </h2>
        <div className="d-flex gap-2">
          <div className="input-group" style={{ width: "300px" }}>
            <span className="input-group-text">
              <FaSearch />
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Search surveys..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <Button variant="outline-secondary" onClick={fetchSurveys}>
            <FaFilter className="me-1" />
            Refresh
          </Button>
        </div>
      </div>

      <Row className="mb-4">
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <Card.Title className="text-primary">{surveys.length}</Card.Title>
              <Card.Text>Total Surveys</Card.Text>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <Card.Title className="text-success">
                {surveys.length > 0
                  ? (
                      surveys.reduce(
                        (acc, survey) =>
                          acc +
                          parseFloat(getAverageRating(survey.answers) || 0),
                        0
                      ) / surveys.length
                    ).toFixed(1)
                  : "0.0"}
              </Card.Title>
              <Card.Text>Avg Rating</Card.Text>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card>
        <Card.Header className="bg-light">
          <h5 className="mb-0">Survey Responses</h5>
        </Card.Header>
        <Card.Body>
          {filteredSurveys.length === 0 ? (
            <div className="text-center py-5">
              <p className="text-muted">No surveys found</p>
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover>
                <thead>
                  <tr>
                    <th>Survey ID</th>
                    <th>User Name</th>
                    <th>User Email</th>
                    <th>Avg Rating</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSurveys.map((survey) => (
                    <tr key={survey.id}>
                      <td>
                        <code>{survey.id.substring(0, 8)}...</code>
                      </td>
                      <td>{survey.userName || "Unknown User"}</td>
                      <td>{survey.userEmail || "No Email"}</td>
                      <td>
                        <Badge
                          bg={
                            parseFloat(getAverageRating(survey.answers)) >= 4
                              ? "success"
                              : parseFloat(getAverageRating(survey.answers)) >=
                                3
                              ? "warning"
                              : "danger"
                          }
                        >
                          {getAverageRating(survey.answers)}
                        </Badge>
                      </td>
                      <td>{formatDate(survey.createdAt)}</td>
                      <td>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => viewSurveyDetails(survey)}
                        >
                          <FaEye className="me-1" />
                          View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Survey Details Modal */}
      <Modal
        show={showSurveyModal}
        onHide={() => setShowSurveyModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Survey Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedSurvey && (
            <div>
              <Row className="mb-3">
                <Col md={6}>
                  <p>
                    <strong>Survey ID:</strong> {selectedSurvey.id}
                  </p>
                  <p>
                    <strong>User:</strong>{" "}
                    {selectedSurvey.userName ||
                      selectedSurvey.userId ||
                      "Unknown User"}
                  </p>
                  <p>
                    <strong>User Email:</strong>{" "}
                    {selectedSurvey.userEmail || "No Email"}
                  </p>
                </Col>
                <Col md={6}>
                  <p>
                    <strong>Date:</strong>{" "}
                    {formatDateTime(selectedSurvey.createdAt)}
                  </p>
                </Col>
              </Row>

              <h6>Survey Responses</h6>
              <div className="table-responsive">
                <Table striped bordered>
                  <thead>
                    <tr>
                      <th>Question</th>
                      <th>Rating</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedSurvey.answers &&
                      Object.entries(selectedSurvey.answers).map(
                        ([questionId, rating]) => (
                          <tr key={questionId}>
                            <td>
                              {questionId === "platform_good" &&
                                "Whether the platform was good"}
                              {questionId === "cost_good" &&
                                "Is the cost reasonable"}
                              {questionId === "vendor_satisfaction" &&
                                "Are you satisfied with the behavior of the vendor"}
                              {questionId === "service_satisfaction" &&
                                "Are you satisfied with service"}
                              {![
                                "platform_good",
                                "cost_good",
                                "vendor_satisfaction",
                                "service_satisfaction",
                              ].includes(questionId) && questionId}
                            </td>
                            <td>
                              <Badge
                                bg={
                                  rating >= 4
                                    ? "success"
                                    : rating >= 3
                                    ? "warning"
                                    : "danger"
                                }
                              >
                                {rating} - {getRatingText(rating)}
                              </Badge>
                            </td>
                          </tr>
                        )
                      )}
                  </tbody>
                </Table>
              </div>

              <div className="mt-3">
                <h6>
                  Average Rating:{" "}
                  <Badge bg="primary">
                    {getAverageRating(selectedSurvey.answers)}
                  </Badge>
                </h6>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSurveyModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SurveysManagement;
